import { useLocation } from "@reach/router"
import classNames from "classnames"
import { navigate } from "gatsby"

import { useAuth } from "../../../auth/hooks"
import { useGetCartQuantity, useGetCartTotal } from "../../../cart/hooks"
import { useHandleSignupCheckout } from "../../../checkout/hooks"
import AnswerButton from "../components/AnswerButton"
import Layout from "../components/Layout"
import SolutionDisplay from "../components/SolutionDisplay"
import { SOLUTION_ITEMS } from "../constants"
import BorderedIconButton from "../SolutionFinder/components/BorderedIconButton"
import { SolutionFilterCategory, SolutionItem } from "../SolutionFinder/types"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import { Progress } from "@components/elements/V2/ProgressBar"
import Typography from "@components/elements/V2/Typography"
import { storyblokHorizontalGutterClasses } from "@constants/spacing"
import { Color } from "@constants/V2/color"
import useIsomorphicLayoutEffect from "@hooks/V2/useIsomorphicLayoutEffect"
import { trackSegmentEvent } from "@utils/analytics"
import { filterOutQueryString } from "@utils/url"
import { getBackgroundColorClass } from "@utils/V2/color"
import React, { useState } from "react"

const CARD_MACHINES: SolutionItem[] = [
  SOLUTION_ITEMS.khumo,
  SOLUTION_ITEMS.khumoPrint,
]

const BUNDLES: SolutionItem[] = [
  SOLUTION_ITEMS.counter,
  SOLUTION_ITEMS.counterPrinter,
  SOLUTION_ITEMS.counterPrinters,
  SOLUTION_ITEMS.counterBarcode,
  SOLUTION_ITEMS.counterPrinterBarcode,
]

const ACCESSORIES: SolutionItem[] = [
  SOLUTION_ITEMS.printer,
  SOLUTION_ITEMS.barcode,
]

const ALL_HARDWARE: SolutionItem[] = [
  ...CARD_MACHINES,
  ...BUNDLES,
  ...ACCESSORIES,
]

const STEP_QUERY_PARAM = "step"
const HARDWARE_STEP_QUERY_VALUE = "hardware"
const HARDWARE_STEP = 2

const HARDWARE_CATEGORIES: SolutionFilterCategory[] = [
  {
    id: "all",
    label: "All",
    items: ALL_HARDWARE,
  },
  { id: "cardMachines", label: "Card machines", items: CARD_MACHINES },
  { id: "bundles", label: "Bundles", items: BUNDLES },
  { id: "accessories", label: "Accessories", items: ACCESSORIES },
]

const CartBuilder = ({ ...props }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [hasSelectedAnswer, setHasSelectedAnswer] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(
    HARDWARE_CATEGORIES[0]
  )
  const location = useLocation()
  const isHardwareStepRequested =
    new URLSearchParams(location.search).get(STEP_QUERY_PARAM) ===
    HARDWARE_STEP_QUERY_VALUE
  const cartQuantity = useGetCartQuantity()
  const cartTotal = useGetCartTotal()
  const { businessId } = useAuth()
  const { isLoading: isHandlingSignupCheckout, handleRedirect } =
    useHandleSignupCheckout()
  const totalSteps = 2
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100
  const cartIsEmpty = cartTotal > 0

  // Not mount-only: navigating here while already mounted reuses the component.
  useIsomorphicLayoutEffect(() => {
    if (isHardwareStepRequested) {
      setCurrentStep(HARDWARE_STEP)
    }
  }, [isHardwareStepRequested])

  const trackChosenPath = (paymentType: "hardware" | "transfer" | "reader") => {
    trackSegmentEvent("Cart Builder Payment Type Chosen", {
      paymentType: paymentType,
    })
  }

  const goToStep = (step: number) => {
    if (step === 1) {
      setHasSelectedAnswer(false)

      if (isHardwareStepRequested) {
        const remainingQuery =
          filterOutQueryString(location.search, [STEP_QUERY_PARAM]) ?? ""

        navigate(`${location.pathname}${remainingQuery}`, { replace: true })
      }
    }

    setCurrentStep(step)

    requestAnimationFrame(() => {
      globalThis.scrollTo({ top: 0, behavior: "smooth" })
    })
  }

  const selectCategory = (category: SolutionFilterCategory) => {
    if (category.id === selectedCategory.id) return

    setSelectedCategory(category)

    trackSegmentEvent("cart-builder-hardware-tab-selected", {
      tab: category.label,
    })
  }

  return (
    <Layout {...props}>
      {currentStep === 1 && (
        <div className="mx-auto flex flex-col items-center px-20 pt-[50px] text-center sm:px-0">
          <Typography
            text="How would you like to get started?"
            font="grotesk"
            weight="medium"
            size="lead-lg"
            color={Color.Black}
            className="mb-10"
          />

          <Typography
            text="Select the option that best describes what you need."
            font="grotesk"
            weight="book"
            size="body-md"
            color={Color.Black}
            className="mb-60 opacity-50"
          />

          <div className="grid w-full max-w-[720px] grid-cols-1 gap-[16px] sm:grid-cols-3">
            <AnswerButton
              imageSrc="https://a.storyblok.com/f/111633/252x189/08b4730758/dedicated-hardware.png"
              text="Buy hardware"
              description="Counter top terminals, card machines, and more"
              disabled={hasSelectedAnswer}
              onClick={() => {
                setHasSelectedAnswer(true)
                trackChosenPath("hardware")

                goToStep(2)
              }}
            />

            <AnswerButton
              imageSrc="https://a.storyblok.com/f/111633/252x189/3bd4d10e70/activatedevice.png"
              text="Activate your device"
              description="New or pre-owned, register it to your account and start accepting payments."
              disabled={hasSelectedAnswer}
              onClick={() => {
                setHasSelectedAnswer(true)
                trackChosenPath("reader")
                globalThis.location.href = `${process.env.GATSBY_SHOP_SIGNUP_URL}?userJourney=register_reader&preSelectedService=reader_registration`
              }}
            />

            <AnswerButton
              imageSrc="https://a.storyblok.com/f/111633/504x378/53dd8f1d71/transfer_device_medium.png"
              text="Transfer your device"
              description="Sold your device to someone else? Transfer it to their business."
              disabled={hasSelectedAnswer}
              onClick={() => {
                setHasSelectedAnswer(true)
                trackChosenPath("transfer")
                globalThis.location.href = `/za/too`
              }}
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div
          className={classNames(
            "pb-0 pt-20 text-center sm:pb-[80px] sm:pt-[50px] lg:pt-[30px]",
            storyblokHorizontalGutterClasses
          )}
        >
          <Typography
            text="What you need to get paid"
            font="grotesk"
            weight="medium"
            size="lead-lg"
            color={Color.Black}
            className="mb-10"
          />

          <Typography
            text="Add your device to start running your business with Yoco."
            font="grotesk"
            weight="book"
            size="body-md"
            color={Color.Black}
            className="mb-40 opacity-50 sm:mb-20"
          />

          <SolutionDisplay
            solution={ALL_HARDWARE}
            filters={{
              categories: HARDWARE_CATEGORIES,
              selectedId: selectedCategory.id,
              onSelect: selectCategory,
            }}
            sortByPriority={false}
            showCartControls
            showNavigationArrows
          />
        </div>
      )}

      <div className="flex items-center justify-center gap-20 pt-[10px] sm:hidden">
        {currentStep > 1 && (
          <BorderedIconButton
            onClick={() => goToStep(currentStep - 1)}
            icon={<ArrowIcon className="rotate-180" />}
            className="h-[50px] w-[50px]"
          />
        )}
      </div>

      <div
        className={classNames(
          "fixed bottom-0 z-30 h-[80px] w-full pb-[20px] sm:flex",
          getBackgroundColorClass(Color.White)
        )}
      >
        <Progress value={progress} className="absolute top-0 w-full" />

        <div
          className={classNames(
            "mt-[20px] flex w-full flex-col items-center sm:flex-row",
            storyblokHorizontalGutterClasses
          )}
        >
          <div className="hidden w-full items-center sm:order-1 sm:flex sm:w-auto">
            {currentStep > 1 && (
              <BorderedIconButton
                onClick={() => goToStep(currentStep - 1)}
                icon={<ArrowIcon className="rotate-180" />}
                text="Back"
                className="w-full gap-10 px-16 py-10 sm:w-auto"
              />
            )}

            {cartIsEmpty && (
              <Typography
                text={`R${cartTotal.toFixed(2)}`}
                font="grotesk"
                weight="medium"
                size="body-lg"
                color={Color.Black}
                className="absolute left-1/2 hidden -translate-x-1/2 md:block"
              />
            )}
          </div>

          <div className="order-1 ml-auto flex w-full pb-0 sm:order-2 sm:w-auto">
            {currentStep === 2 && (
              <PillButton
                style="solid"
                size="small"
                color={Color.Blue}
                text="Continue to checkout"
                disabled={cartQuantity === 0 || isHandlingSignupCheckout}
                onClick={() => {
                  trackSegmentEvent("cart-builder-review-order-clicked")
                  handleRedirect(businessId ?? undefined)
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default CartBuilder
